import { AlertTriangle, CheckCircle2, Clock, Database, ExternalLink, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  formatLiveTermsDate,
  getLiveTermsStatus,
  type ProviderLiveTerms,
} from "@/hooks/useProviderLiveTerms";

interface LiveDataStatusProps {
  terms?: ProviderLiveTerms | null;
  compact?: boolean;
  className?: string;
}

const statusCopy = {
  fresh: {
    label: "Live data",
    description: "Villkoren hämtas automatiskt från aktuell källa.",
    icon: CheckCircle2,
    className: "border-success/40 bg-success/10 text-success",
  },
  stale: {
    label: "Uppgifter kontrolleras",
    description: "Vi visar senast kända villkor och uppdaterar sidan när källan har verifierats igen.",
    icon: Clock,
    className: "border-amber-500/40 bg-amber-500 text-amber-500",
  },
  needs_review: {
    label: "Behöver granskas",
    description: "Nya uppgifter har hämtats och kontrolleras innan de visas som aktuella villkor.",
    icon: AlertTriangle,
    className: "border-amber-500/40 bg-amber-500 text-amber-500",
  },
  failed: {
    label: "Uppgifter kontrolleras",
    description: "Vi visar senast kända villkor medan källan kontrolleras på nytt.",
    icon: RefreshCw,
    className: "border-muted bg-muted/60 text-muted-foreground",
  },
  missing: {
    label: "Uppgifter kontrolleras",
    description: "Villkoren kontrolleras löpande och uppdateras när verifierade uppgifter finns.",
    icon: Database,
    className: "border-muted bg-muted/60 text-muted-foreground",
  },
};

export function LiveDataStatus({ terms, compact = false, className = "" }: LiveDataStatusProps) {
  const status = getLiveTermsStatus(terms);
  const copy = statusCopy[status];
  const Icon = copy.icon;
  const lastVerifiedAt = terms?.last_verified_at || terms?.last_scraped_at;
  const lastVerified = lastVerifiedAt ? formatLiveTermsDate(lastVerifiedAt) : null;

  if (compact) {
    if (status !== "fresh" && !lastVerified && !terms?.source_name) {
      return null;
    }

    return (
      <div className={`flex flex-wrap items-center gap-2 text-xs text-muted-foreground ${className}`}>
        <Badge variant="outline" className={copy.className}>
          <Icon className="mr-1.5 h-3.5 w-3.5" />
          {copy.label}
        </Badge>
        {lastVerified && <span>Kontrollerad {lastVerified}</span>}
        {terms?.source_name && (
          <span className="inline-flex items-center gap-1">
            Källa: {terms.source_name}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${copy.className} ${className}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold">{copy.label}</p>
            {terms?.source_name && <Badge variant="outline">Källa: {terms.source_name}</Badge>}
          </div>
          <p className="mt-1 text-sm opacity-90">{copy.description}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs opacity-90">
            {lastVerified && <span>Senast kontrollerad {lastVerified}</span>}
            {terms?.confidence !== undefined && (
              <span>Säkerhet {Math.round(terms.confidence * 100)}%</span>
            )}
            {terms?.source_url && (
              <a
                href={terms.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
              >
                Öppna källa
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
